import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Briefcase, ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
import PricingDisclosure from "@/components/PricingDisclosure";
import api, { formatApiError } from "@/lib/api";

const AIRPORT_CHIPS = [
  "Boston Logan (BOS)",
  "Manchester Regional (MHT)",
  "Bradley International (BDL)",
  "T.F. Green (PVD)",
  "John F. Kennedy (JFK)",
];

const CATEGORY_META = {
  Luxury_Sedan: {
    label: "Luxury Sedan",
    tagline: "Discreet executive travel for one to three guests.",
    order: 1,
  },
  Premium_SUV: {
    label: "Premium SUV",
    tagline: "Full-size luxury for families, groups, and luggage-heavy trips.",
    order: 2,
  },
  Executive_Sprinter: {
    label: "Executive Sprinter",
    tagline: "First-class lounge seating for corporate and group travel.",
    order: 3,
  },
  Motor_Coach: {
    label: "Motor Coach",
    tagline: "Up to 56 passengers for events, weddings, and tours.",
    order: 4,
  },
};

export default function Fleet() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    api
      .get("/vehicles")
      .then(({ data }) => {
        if (alive) setVehicles(data || []);
      })
      .catch((e) => {
        if (alive) setError(formatApiError(e));
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  // Group by category
  const grouped = Object.keys(CATEGORY_META)
    .map((cat) => ({
      category: cat,
      meta: CATEGORY_META[cat],
      items: vehicles.filter((v) => v.category === cat),
    }))
    .sort((a, b) => a.meta.order - b.meta.order);

  return (
    <Layout>
      {/* Hero strip */}
      <section
        data-testid="fleet-hero"
        className="relative pt-40 pb-20 bg-[#0B0B0C] overflow-hidden"
      >
        <div
          className="absolute inset-0 opacity-40 bg-cover bg-center"
          style={{ backgroundImage: "url('/assets/fleet-grid.jpg')" }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0C]/55 via-[#0B0B0C]/70 to-[#0B0B0C]/95" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
          <div className="text-[11px] uppercase tracking-[0.4em] text-[#8E9AA6] mb-6">
            The Fleet
          </div>
          <h1
            data-testid="fleet-heading"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter text-white"
          >
            A vehicle for every occasion.
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-sm md:text-base text-[#8E9AA6] font-light">
            Twelve immaculately maintained vehicles — sedans, SUVs, sprinters, and motor
            coaches — all chauffeured by our executive drivers.
          </p>
        </div>
      </section>

      {/* Grid */}
      <section className="relative bg-[#0B0B0C] pb-32 overflow-hidden">
        <div
          className="absolute inset-0 opacity-15 bg-cover bg-center bg-fixed"
          style={{ backgroundImage: "url('/assets/fleet-grid.jpg')" }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-[#0B0B0C]/85" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div
            data-testid="fleet-coverage-chips"
            className="flex flex-wrap justify-center gap-2 mb-14"
          >
            {AIRPORT_CHIPS.map((c) => (
              <span
                key={c}
                className="inline-block px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] text-[#E6E6E6] border border-white/15 bg-white/[0.03]"
              >
                {c}
              </span>
            ))}
          </div>

          {loading && (
            <div
              data-testid="fleet-loading"
              className="text-center text-[#8E9AA6] py-24 text-sm uppercase tracking-[0.3em]"
            >
              Loading fleet…
            </div>
          )}

          {error && !loading && (
            <div
              data-testid="fleet-error"
              className="text-center text-red-400 py-12 text-sm"
            >
              {error}
            </div>
          )}

          {!loading && !error && (
            <div
              data-testid="fleet-grid"
              className="grid grid-cols-1 md:grid-cols-2 gap-8"
            >
              {grouped.map(({ category, meta, items }) => {
                const samples = items.slice(0, 3);
                const maxPax = items.reduce(
                  (m, v) => Math.max(m, v.capacity_passengers || 0),
                  0
                );
                const maxLug = items.reduce(
                  (m, v) => Math.max(m, v.capacity_luggage || 0),
                  0
                );
                return (
                  <div
                    key={category}
                    data-testid={`fleet-card-${category.toLowerCase()}`}
                    className="group relative backdrop-blur-xl bg-[#121214]/60 border border-white/10 p-10 md:p-12 transition-all duration-500 hover:-translate-y-2 hover:border-white/30 hover:shadow-[0_20px_60px_-15px_rgba(255,255,255,0.06)]"
                  >
                    <div className="flex items-start justify-between mb-8">
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.35em] text-[#8E9AA6] mb-3">
                          Category 0{meta.order}
                        </div>
                        <div className="font-serif text-3xl md:text-4xl font-semibold tracking-tight text-white">
                          {meta.label}
                        </div>
                      </div>
                      <div className="text-right text-xs uppercase tracking-[0.2em] text-[#8E9AA6]">
                        {items.length}
                        <div className="text-[10px] mt-1">in fleet</div>
                      </div>
                    </div>

                    <p className="text-sm text-[#E6E6E6] font-light leading-relaxed mb-10">
                      {meta.tagline}
                    </p>

                    <div className="border-t border-white/10 pt-8 mb-8">
                      <div className="text-[11px] uppercase tracking-[0.3em] text-[#8E9AA6] mb-4">
                        Featuring
                      </div>
                      <ul className="space-y-2">
                        {samples.map((v) => (
                          <li
                            key={v.id}
                            className="flex items-baseline justify-between gap-4 text-sm text-white font-light"
                          >
                            <span>
                              {v.make} {v.model}
                            </span>
                            <span className="text-[#8E9AA6] text-xs tracking-wider">
                              {v.year}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="flex items-center justify-between border-t border-white/10 pt-6">
                      <div className="flex gap-8">
                        <div className="flex items-center gap-2 text-[#E6E6E6]">
                          <Users size={16} strokeWidth={1.25} />
                          <span className="text-sm">Up to {maxPax}</span>
                        </div>
                        <div className="flex items-center gap-2 text-[#E6E6E6]">
                          <Briefcase size={16} strokeWidth={1.25} />
                          <span className="text-sm">Up to {maxLug}</span>
                        </div>
                      </div>
                      <Link
                        to="/book"
                        data-testid={`fleet-reserve-${category.toLowerCase()}`}
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white hover:text-[#8E9AA6] transition-colors"
                      >
                        Reserve <ArrowRight size={14} strokeWidth={1.5} />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!loading && !error && (
            <div className="mt-16 max-w-3xl mx-auto">
              <PricingDisclosure testid="fleet-pricing-disclosure" />
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
